import React, { FunctionComponent, useCallback } from 'react'
import Grid from '@sport1/react-elements/Grid'
import LabelFont from '../../Theme/Fonts/Label'
import Label10Font from '../../Theme/Fonts/Label10'
import { TestID } from '../../utils/PropTypesDefinitions'
import SegmentedControlButton from './HelperClass/SegmentedControlButton'

export type DefaultSegmentProps = {
    /**
     * Text which will be shown on first button
     * */
    firstLabel: string
    /**
     * Text which will be shown on second button
     * */
    secondLabel: string
    /***
     * Function that will be triggered when button clicked using index number of button 0 | 1 | ...
     * @TJS-type function
     * */
    onPress: (index: number) => void
    /**
     * The button index which should be pressed when compnent loaded
     * */
    initialSelectedIndex?: number
} & TestID

interface TwoSegmentedType extends DefaultSegmentProps {
    /**
     * number of buttons will appear
     * */
    type: 'TWO'
    /**
     * Show/Hide text between two button which is specific to TWO segmented control
     * */
    hideAdLabel?: boolean
    /**
     * No thirdlabel for TWO segmented control
     * */
    thirdLabel?: never
    /**
     * a text message which shown under buttons when specific button is clicked
     * */
    additionalMessage?: string
    /**
     * index of button which control additionalMessage visibility
     * */
    additionalMessageIndex?: 0 | 1
}

interface ThreeSegmentedType extends DefaultSegmentProps {
    /**
     * number of buttons will appear
     * */
    type: 'THREE'
    /**
     * Text which will be shown on third button
     * */
    thirdLabel: string
    /**
     * There wont be any middle label in THREE segmented control
     * */
    hideAdLabel?: never
    /**
     * There wont be any additionalMessage in THREE segmented control
     * */
    additionalMessage?: never
    /**
     * There wont be any additionalMessageIndex in THREE segmented control
     * */
    additionalMessageIndex?: never
}

export type SegmentedControlProps = TwoSegmentedType | ThreeSegmentedType

const SegmentedControl: FunctionComponent<SegmentedControlProps> = ({
    type,
    firstLabel,
    secondLabel,
    thirdLabel,
    onPress,
    initialSelectedIndex = 0,
    hideAdLabel,
    additionalMessage,
    additionalMessageIndex,
    testID,
}) => {
    const [selectedIndex, setSelectedIndex] = React.useState(initialSelectedIndex)
    const onPressCallback = useCallback(
        (index: number): void => {
            setSelectedIndex(index)
            onPress(index)
        },
        [onPress]
    )
    return (
        <Grid testID={testID} noExpand>
            <Grid
                horizontal
                backgroundColor="tahitian-3"
                borderRadius={18}
                height={36}
                paddingX={1}
                paddingY={1}
                noExpand
            >
                <SegmentedControlButton
                    label={firstLabel}
                    onPress={(): void => onPressCallback(0)}
                    selected={selectedIndex === 0}
                    testID={`${testID}_Button_0`}
                />

                {type === 'TWO' ? (
                    <Grid width={32} center noExpand centerVertical>
                        {!hideAdLabel ? (
                            <LabelFont allowFontScaling={false} color="keshi-3">
                                -w-
                            </LabelFont>
                        ) : null}
                    </Grid>
                ) : null}

                <SegmentedControlButton
                    label={secondLabel}
                    onPress={(): void => onPressCallback(1)}
                    selected={selectedIndex === 1}
                    testID={`${testID}_Button_1`}
                />

                {thirdLabel && type === 'THREE' ? (
                    <SegmentedControlButton
                        label={thirdLabel}
                        onPress={(): void => onPressCallback(2)}
                        selected={selectedIndex === 2}
                        testID={`${testID}_Button_2`}
                    />
                ) : null}
            </Grid>

            {additionalMessage && type === 'TWO' ? (
                <Grid noExpand centerVertical height={20}>
                    {additionalMessageIndex === undefined ||
                    additionalMessageIndex === selectedIndex ? (
                        <Label10Font
                            color="keshi-3"
                            textAlign="right"
                        >{`* ${additionalMessage}`}</Label10Font>
                    ) : null}
                </Grid>
            ) : null}
        </Grid>
    )
}

export default SegmentedControl
